import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

/**
 * Extracts style attributes from HTML elements containing video URLs
 * @param {string} content - HTML content
 * @param {string} url - Video URL to find context for
 * @returns {string} - Extracted style string or empty string
 */
const extractVideoStyles = (content, url) => {
  if (!content || !url) return '';

  // Look for the URL within styled elements
  const stylePatterns = [
    // Match div/p with style containing the URL
    new RegExp(`<(div|p)[^>]*style="([^"]*)"[^>]*>[^<]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
    // Match URL wrapped in styled anchor tag
    new RegExp(`<a[^>]*style="([^"]*)"[^>]*href="[^"]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
    // Match styled elements containing the URL
    new RegExp(`<[^>]*style="([^"]*)"[^>]*>[^<]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i')
  ];

  for (const pattern of stylePatterns) {
    const match = content.match(pattern);
    if (match && (match[1] || match[2])) {
      return match[1] || match[2];
    }
  }

  return '';
};

/**
 * Merges database styles with default video container styles
 * @param {string} dbStyles - Styles from database content
 * @param {string} defaultStyles - Default video container styles
 * @returns {string} - Merged style string
 */
const mergeVideoStyles = (dbStyles, defaultStyles) => {
  if (!dbStyles) return defaultStyles;

  // Parse existing styles to avoid conflicts
  const dbStyleObj = {};
  const defaultStyleObj = {};

  // Parse database styles
  dbStyles.split(';').forEach(style => {
    const [key, value] = style.split(':').map(s => s.trim());
    if (key && value) {
      dbStyleObj[key] = value;
    }
  });

  // Parse default styles
  defaultStyles.split(';').forEach(style => {
    const [key, value] = style.split(':').map(s => s.trim());
    if (key && value) {
      defaultStyleObj[key] = value;
    }
  });

  // Merge (database styles take precedence for non-critical properties)
  const merged = { ...defaultStyleObj, ...dbStyleObj };

  // Ensure critical video properties are preserved
  merged['position'] = defaultStyleObj['position'] || 'relative';
  merged['width'] = defaultStyleObj['width'] || '100%';
  merged['height'] = defaultStyleObj['height'] || '0';
  merged['padding-bottom'] = defaultStyleObj['padding-bottom'] || '56.25%';

  return Object.entries(merged)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
};

// Video platform detection and conversion utilities
const VIDEO_PLATFORMS = {
  YOUTUBE: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ],
    getEmbedUrl: (videoId) => `https://www.youtube.com/embed/${videoId}`,
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://www.youtube.com/embed/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        </div>
      `;
    }
  },
  VIMEO: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
      /(?:https?:\/\/)?(?:player\.)?vimeo\.com\/video\/(\d+)/
    ],
    getEmbedUrl: (videoId) => `https://player.vimeo.com/video/${videoId}`,
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://player.vimeo.com/video/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen; picture-in-picture"
          ></iframe>
        </div>
      `;
    }
  },
  DIRECT_VIDEO: {
    patterns: [
      /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|mov|avi|wmv|flv|m4v)(?:\?[^\s]*)?$/i
    ],
    getEmbedHtml: (url, dbStyles = '') => {
      const defaultContainerStyles = 'margin: 1rem 0; width: 100%; max-width: 100%';
      const defaultVideoStyles = 'width: 100%; max-width: 100%; height: auto; display: block';
      const mergedContainerStyles = mergeVideoStyles(dbStyles, defaultContainerStyles);
      return `
        <div style="${mergedContainerStyles}">
          <video
            controls
            style="${defaultVideoStyles}"
            preload="metadata"
            playsinline
          >
            <source src="${url}" type="video/${url.split('.').pop().split('?')[0]}">
            Your browser does not support the video tag.
            <a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline-block; margin-top: 0.5rem; color: #1976d2; text-decoration: none;">Download video</a>
          </video>
        </div>
      `;
    }
  },
  DAILYMOTION: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/
    ],
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://www.dailymotion.com/embed/video/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen"
          ></iframe>
        </div>
      `;
    }
  },
  TWITCH: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/,
      /(?:https?:\/\/)?(?:clips\.)?twitch\.tv\/([a-zA-Z0-9_-]+)(?:\/clip\/([a-zA-Z0-9_-]+))?/
    ],
    getEmbedHtml: (videoId, clipId, dbStyles = '') => {
      const embedUrl = clipId
        ? `https://clips.twitch.tv/embed?clip=${clipId}&parent=${window.location.hostname}`
        : `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`;

      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);

      return `
        <div style="${mergedStyles}">
          <iframe
            src="${embedUrl}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen"
          ></iframe>
        </div>
      `;
    }
  },
  GENERIC_FALLBACK: {
    patterns: [
      /https?:\/\/[^\s]*(?:video|watch|embed|play)[^\s]*/i
    ],
    getEmbedHtml: (url, dbStyles = '') => {
      const defaultStyles = 'margin: 1rem 0; padding: 1rem; border: 1px solid #e1e5e9; border-radius: 8px; background-color: #f8f9fa';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <p style="margin: 0 0 0.5rem 0; font-weight: 600;">Video Link:</p>
          <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none;">
            ${url.length > 60 ? url.substring(0, 60) + '...' : url}
          </a>
          <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem; color: #666;">
            Click to open video in new tab
          </p>
        </div>
      `;
    }
  }
};

/**
 * Detects video URLs and converts them to appropriate embed code
 * @param {string} content - HTML content that may contain video links
 * @returns {string} - Content with video links converted to embeds
 */
const convertVideoLinksToEmbeds = (content) => {
  if (!content) return content;

  let processedContent = content;
  const processedUrls = new Set(); // Avoid processing the same URL multiple times

  // Process each video platform (order matters - more specific patterns first)
  const platformOrder = ['YOUTUBE', 'VIMEO', 'DAILYMOTION', 'TWITCH', 'DIRECT_VIDEO', 'GENERIC_FALLBACK'];

  platformOrder.forEach(platform => {
    const config = VIDEO_PLATFORMS[platform];
    if (!config) return;

    config.patterns.forEach(pattern => {
      // Handle URLs wrapped in anchor tags
      const anchorRegex = new RegExp(`<a[^>]*href=['"]([^'"]*?)['"][^>]*>([^<]*)</a>`, 'gi');
      processedContent = processedContent.replace(anchorRegex, (match, url, linkText) => {
        if (processedUrls.has(url)) return match;

        const videoMatch = url.match(pattern);
        if (videoMatch) {
          processedUrls.add(url);
          const dbStyles = extractVideoStyles(processedContent, url);

          if (platform === 'DIRECT_VIDEO' || platform === 'GENERIC_FALLBACK') {
            return config.getEmbedHtml(url, dbStyles);
          } else if (platform === 'TWITCH') {
            return config.getEmbedHtml(videoMatch[1], videoMatch[2], dbStyles);
          } else {
            return config.getEmbedHtml(videoMatch[1], dbStyles);
          }
        }
        return match;
      });

      // Handle plain URLs (not wrapped in anchor tags)
      processedContent = processedContent.replace(pattern, (match, ...groups) => {
        if (processedUrls.has(match)) return match;

        // Skip if this URL is already inside an HTML tag
        const beforeMatch = processedContent.substring(0, processedContent.indexOf(match));
        const afterMatch = processedContent.substring(processedContent.indexOf(match) + match.length);
        if (beforeMatch.lastIndexOf('<') > beforeMatch.lastIndexOf('>') ||
            afterMatch.indexOf('>') < afterMatch.indexOf('<')) {
          return match;
        }

        processedUrls.add(match);
        const dbStyles = extractVideoStyles(processedContent, match);

        if (platform === 'DIRECT_VIDEO' || platform === 'GENERIC_FALLBACK') {
          return config.getEmbedHtml(match, dbStyles);
        } else if (platform === 'TWITCH') {
          return config.getEmbedHtml(groups[0], groups[1], dbStyles);
        } else {
          return config.getEmbedHtml(groups[0], dbStyles);
        }
      });
    });
  });

  return processedContent;
};

const RichTextRenderer = ({ 
  content, 
  className = '', 
  style = {},
  fontSize = '16px',
  lineHeight = '1.6',
  maxWidth = 'none'
}) => {
  const containerRef = useRef(null);
  const shadowRootRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create Shadow DOM if not exists
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
    }

    const shadowRoot = shadowRootRef.current;

    // Responsive styles for all media elements
    const styles = `
      <style>
        :host {
          display: block;
        }

        /* Only neutralize headings that have no explicit styling */
        h1:not([style]), h2:not([style]), h3:not([style]),
        h4:not([style]), h5:not([style]), h6:not([style]) {
          font-weight: normal;
        }

        /* Preserve all database styles and semantic tags */
        [style] {
          /* Database inline styles take full precedence */
        }

        strong, b {
          font-weight: bold;
        }

        /* Main container */
        .rich-content {
          display: block;
          width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }

        /* Responsive Images */
        .rich-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0.5rem 0;
        }

        /* Responsive Videos (HTML5) */
        .rich-content video {
          max-width: 100%;
          height: auto;
          display: block;
        }

        /* Responsive Tables */
        .rich-content table {
          width: 100%;
          border-collapse: collapse;
          overflow-x: auto;
          display: block;
          white-space: nowrap;
        }

        /* Table wrapper for horizontal scroll on mobile */
        .rich-content table {
          display: table;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Responsive table container */
        .table-responsive {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .rich-content {
            font-size: 14px;
            line-height: 1.5;
          }

          /* Smaller margins on mobile */
          .rich-content > * {
            margin-top: 0.75rem;
            margin-bottom: 0.75rem;
          }

          /* Responsive video containers */
          .rich-content iframe,
          .rich-content video {
            max-width: 100%;
            height: auto;
          }

          /* Mobile image enhancements */
          .rich-content img {
            max-width: 100%;
            width: auto !important;
            height: auto !important;
            margin: 0.75rem 0;
            cursor: pointer;
            transition: transform 0.2s ease;
          }

          .rich-content img:hover {
            transform: scale(1.02);
          }

          .rich-content img.zoomed {
            transform: scale(1.5);
            z-index: 10;
            position: relative;
          }

          /* Table responsive behavior */
          .rich-content table {
            font-size: 12px;
            display: block;
            overflow-x: auto;
            white-space: nowrap;
            width: 100%;
          }

          .rich-content th,
          .rich-content td {
            padding: 0.5rem 0.25rem;
            white-space: nowrap;
          }
        }

        /* Extra small screens */
        @media (max-width: 480px) {
          .rich-content {
            font-size: 13px;
          }

          .rich-content table {
            font-size: 11px;
          }

          .rich-content th,
          .rich-content td {
            padding: 0.25rem 0.125rem;
          }
        }
      </style>
    `;

    // Convert video links to embeds before sanitization
    const contentWithVideoEmbeds = convertVideoLinksToEmbeds(content || '<p>No content available</p>');

    // Sanitize content and create DOM structure
    const sanitizedContent = DOMPurify.sanitize(contentWithVideoEmbeds, {
      ALLOWED_TAGS: [
        // Text elements
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'mark', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'img', 'hr', 'div', 'span', 'sub', 'sup',
        // Video elements
        'iframe', 'video', 'source'
      ],
      ALLOWED_ATTR: [
        // Standard attributes
        'href', 'target', 'title', 'alt', 'src', 'width', 'height', 'class', 'id', 'style',
        // Video-specific attributes
        'controls', 'autoplay', 'muted', 'loop', 'poster', 'preload', 'type',
        // Iframe-specific attributes
        'frameborder', 'allowfullscreen', 'allow', 'loading', 'sandbox'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      // Allow YouTube and Vimeo domains for iframe embeds
      ALLOWED_SCHEMES: ['http', 'https', 'data'],
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
    });

    shadowRoot.innerHTML = `${styles}<div class="rich-content">${sanitizedContent}</div>`;

    // Ensure all inline styles are properly applied in Shadow DOM
    const styledElements = shadowRoot.querySelectorAll('[style]');
    styledElements.forEach(element => {
      const styleAttr = element.getAttribute('style');
      if (styleAttr) {
        element.style.cssText = styleAttr;
      }
    });

    // Post-process for responsive media elements
    const processResponsiveMedia = () => {
      // Make images responsive if they don't have explicit responsive styles
      const images = shadowRoot.querySelectorAll('img');
      images.forEach(img => {
        const currentStyle = img.getAttribute('style') || '';
        if (!currentStyle.includes('max-width') && !currentStyle.includes('width')) {
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.style.display = 'block';
        }

        // Remove any fixed width/height attributes that could cause overflow on mobile
        if (img.hasAttribute('width')) {
          img.setAttribute('data-original-width', img.getAttribute('width'));
        }
        if (img.hasAttribute('height')) {
          img.setAttribute('data-original-height', img.getAttribute('height'));
        }

        // Apply responsive behavior
        const applyResponsiveStyles = () => {
          const isMobile = window.innerWidth <= 640;
          if (isMobile) {
            img.removeAttribute('width');
            img.removeAttribute('height');
            img.style.maxWidth = '100%';
            img.style.width = 'auto';
            img.style.height = 'auto';
          }
        };

        // Apply on load and resize
        applyResponsiveStyles();
        window.addEventListener('resize', applyResponsiveStyles);

        // Add touch-friendly zoom on mobile
        img.addEventListener('click', (e) => {
          const isMobile = window.innerWidth <= 640;
          if (isMobile) {
            e.preventDefault();
            img.classList.toggle('zoomed');

            // Remove zoom after 3 seconds
            if (img.classList.contains('zoomed')) {
              setTimeout(() => {
                img.classList.remove('zoomed');
              }, 3000);
            }
          }
        });
      });

      // Wrap tables for responsive horizontal scrolling
      const tables = shadowRoot.querySelectorAll('table');
      tables.forEach(table => {
        if (!table.parentElement.classList.contains('table-responsive')) {
          const wrapper = document.createElement('div');
          wrapper.className = 'table-responsive';
          wrapper.style.cssText = 'width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 1rem 0;';
          table.parentNode.insertBefore(wrapper, table);
          wrapper.appendChild(table);
        }
      });

      // Ensure iframes are properly constrained
      const iframes = shadowRoot.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (!iframe.parentElement.style.position) {
          iframe.style.maxWidth = '100%';
          iframe.style.height = 'auto';
        }
      });
    };

    processResponsiveMedia();

    // Handle link clicks within shadow DOM
    const links = shadowRoot.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('http')) {
          e.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      });
    });

  }, [content, fontSize, lineHeight, maxWidth]);

  return (
    <div 
      ref={containerRef}
      className={`rich-text-renderer ${className}`}
      style={style}
      role="article"
      aria-label="Rich text content"
    />
  );
};

export default RichTextRenderer;